import React from "react";
import type { ApolloClient } from "@apollo/client";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { createLoadingSelector } from "store/selectors";
import { adminConstants } from "actions/types";
import { Card, Col, Row } from "react-bootstrap";
import {
  getPaginatedCustomReports,
  getReportsStatus,
} from "actions/adminActions";
import { FiFolder, FiPlus } from "react-icons/fi";

import Button from "components/Button";
import IconSpinner from "components/IconSpinner";
import IconTable, { type IconTableColumn } from "components/IconTable";
import IconTableHeader from "components/IconTableHeader";
import IconMessageCard from "components/IconMessageCard";
import Paginator from "components/Paginator";
import { push } from "store/actions/router";
import { getReportCSV } from "services/adminService";
import moment from "moment";
import { chain, get, isEmpty, some } from "lodash";
import { downloadCSVFromString } from "utils/dom";
import EllipsisLoader from "components/EllipsisLoader";
import PromisePoller from "components/PromisePoller";
import type { RootState } from "types/state";

// `report.status` from the gql selection is `string | null`; widen the element
// type so `.includes(report.status)` keeps its original runtime behavior
// (`["REQUESTED","PROCESSING"].includes(null) === false`).
const ASYNC_STATUSES: Array<string | null> = ["REQUESTED", "PROCESSING"];

// Runtime shape of a custom report row, matching the getCustomReports gql
// selection (services/adminService.ts). The redux slice types `results` as
// `unknown[]` (types/state.ts AdminPaginated), so we narrow at the
// mapStateToProps boundary below.
interface CustomReport {
  id: string | null;
  locationUri: string | null;
  status: string | null;
  reportRequest: {
    reportName: string | null;
    requestedBy: string | null;
    reportDefinition: {
      reportType: string | null;
      description: string | null;
    } | null;
  } | null;
}

const isFetching = createLoadingSelector(adminConstants.GET_CUSTOM_REPORTS);
const isFetchingStatuses = createLoadingSelector(
  adminConstants.GET_REPORTS_STATUS
);

const mapStateToProps = (state: RootState) => {
  const { results, totalRows } = state.admin.customReports;
  return {
    // AdminPaginated.results is `unknown[]`; narrow to the gql-selected shape.
    reports: results as CustomReport[],
    totalRows,
    isFetching: isFetching(state),
    isFetchingStatuses: isFetchingStatuses(state),
  };
};

const mapDispatchToProps = {
  getPaginatedCustomReports,
  push,
  getReportsStatus,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = WithApolloClient<PropsFromRedux>;

interface State {
  limit: number;
  offset: number;
  initialFetching: boolean;
  page: number;
}

class CustomReports extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      limit: 50,
      offset: 0,
      initialFetching: true,
      page: 0,
    };
  }

  componentDidMount() {
    void this._getPaginatedCustomReports().then(() => {
      this.setState({
        initialFetching: false,
      });
    });
  }

  _onPageChange = ({ selected }: { selected: number }) => {
    const offset = selected * this.state.limit;

    this.setState({ page: selected, offset }, () =>
      this._getPaginatedCustomReports()
    );
  };

  _getPaginatedCustomReports = () => {
    return this.props.getPaginatedCustomReports(
      this.props.client as ApolloClient<object>,
      {
        limit: this.state.limit,
        offset: this.state.offset,
      }
    );
  };

  _downloadCSV = async (report: CustomReport) => {
    const { id: reportId } = report;
    const reportType = get(report, "reportRequest.reportDefinition.reportType");
    const reportName = `${reportType}-${moment().format("YYYY-MM-DD")}.csv`;
    const csvString = await getReportCSV(
      this.props.client as ApolloClient<object>,
      reportId as string
    );
    downloadCSVFromString(csvString, reportName);
  };

  _reportColumns = (): Array<IconTableColumn<CustomReport>> => {
    const columns: Array<IconTableColumn<CustomReport>> = [
      {
        label: "Report Name",
        key: "reportRequest.reportName",
      },
      {
        label: "Report Type",
        key: "reportRequest.reportDefinition.description",
      },
      {
        label: "Report Status",
        key: "status",
        customComponent: (props) => {
          const status = get(props, "status", "UNKNOWN");
          return (
            <div className={"async-status-field"}>
              {status}{" "}
              {ASYNC_STATUSES.includes(status) ? <EllipsisLoader /> : null}
            </div>
          );
        },
      },
      {
        customComponent: (props) => {
          if (!props.locationUri) return null;
          return (
            <span style={{ textAlign: "center", width: 100 }}>
              <Button
                btnLabel="Download CSV"
                name="action"
                color="action"
                size="sm"
                type="button"
                onClick={() => {
                  void this._downloadCSV(props);
                }}
              />
            </span>
          );
        },
      },
    ];
    return columns;
  };

  _getBlankSlate = () => {
    return (
      <Row>
        <Col>
          <IconMessageCard
            header="No Reports"
            subheader="It looks like we have not generated any reports yes"
            icon={<FiFolder color="white" stroke="#60A4BF" size={16} />}
          />
        </Col>
      </Row>
    );
  };

  getReportsStatus = () => {
    const reportIds = chain(this.props.reports)
      .filter((report) => {
        return ASYNC_STATUSES.includes(report.status);
      })
      .map("id")
      .value() as string[];

    void this.props.getReportsStatus(
      this.props.client as ApolloClient<object>,
      reportIds
    );
  };

  render() {
    if (this.state.initialFetching) {
      return <IconSpinner centered />;
    }

    const columns = this._reportColumns();
    const data = this.props.reports;
    const noReports = isEmpty(data);
    const shouldPollReports =
      !noReports &&
      some(data, (report) => {
        return ASYNC_STATUSES.includes(report.status);
      });

    return (
      <div id="admin-reports">
        <h2 style={{ color: "#0a2540" }}>Custom Reports</h2>
        <Card>
          <Row>
            <Col>
              {shouldPollReports && (
                <PromisePoller
                  interval={5000}
                  minutesToPollFor={10}
                  // @ts-expect-error found-not-fixed: getReportsStatus dispatches
                  // but does not `return` the resulting promise, so it is typed
                  // `() => void`; PromisePoller.promiseToPoll expects
                  // `() => Promise<unknown>`. Preserving the original behavior
                  // (poller receives undefined) rather than adding a `return`.
                  promiseToPoll={this.getReportsStatus}
                />
              )}
              <IconTableHeader
                tableHeader={"Admin Reports"}
                tableCountLabel="Reports"
                tableCount={this.props.totalRows}
              />
              <div
                style={{
                  float: "right",
                  margin: 8,
                }}
              >
                <Button
                  icon={{
                    icon: <FiPlus size={14} color={"#fffff"} />,
                    position: "left",
                  }}
                  size="sm"
                  name="action"
                  onClick={() =>
                    this.props.push("/dashboard/reports/custom/new")
                  }
                  btnLabel="New Report"
                />
              </div>
              {!noReports && (
                <>
                  <IconTable columns={columns} data={data} />
                  <Paginator
                    onChange={this._onPageChange}
                    pageCount={this.props.totalRows / this.state.limit}
                    page={this.state.page}
                  />
                </>
              )}
              {noReports && this._getBlankSlate()}
            </Col>
          </Row>
        </Card>
      </div>
    );
  }
}

export default connector(withApollo<Props>(CustomReports));
